/**
 * A behavior schema as text for a model: what `list_behaviors` returns and
 * what a behavior specialist reads before it writes data.
 */
import type { BehaviorSchema, FieldSchema } from './types';

/** One field as `key: type = default — description`. */
function formatField(field: FieldSchema, indent: string): string {
    let type: string;
    if (field.type === 'enum' || field.type === 'multiSelect') {
        type = `${field.type} [${field.options.join(' | ')}]`;
    } else if (field.type === 'object' && field.isMap) {
        type = 'Record<string, object>';
    } else if (field.type === 'string' && field.arrayOf) {
        type = `${field.arrayOf}[]`;
    } else {
        type = field.type;
    }
    let line = `${indent}${field.key}: ${type}`;
    if (field.default !== undefined) line += ` = ${JSON.stringify(field.default)}`;
    if (field.description) line += ` — ${field.description}`;
    return line;
}

/**
 * The schema with its fields first: the description's first paragraph, every
 * visible field (one level of nesting, including a number's object form), the
 * actions, then the rest of the description and the generated docs. Hosts cap
 * tool results, and a cut must cost prose, never fields: Text's docs alone run
 * past 10,000 characters.
 */
export function formatBehaviorSchema(schema: BehaviorSchema): string {
    const lines: string[] = [`## ${schema.type}`];
    const [lead, ...rest] = (schema.description ?? '').split(/\n\s*\n/);
    const remainder = rest.join('\n\n').trim();
    if (lead) lines.push(lead);
    lines.push('');
    for (const field of schema.fields) {
        if (field.hidden) continue;
        lines.push(formatField(field, '  '));
        if (field.type === 'object' && field.fields.length > 0) {
            if (field.isMap) lines.push('    (each value is an object with:)');
            for (const sub of field.fields) if (!sub.hidden) lines.push(formatField(sub, '    '));
        }
        if (field.type === 'number' && field.objectFields) {
            lines.push('    (or an object with:)');
            for (const sub of field.objectFields) if (!sub.hidden) lines.push(formatField(sub, '    '));
        }
        if (field.type === 'array' && field.itemFields) {
            lines.push('    (array items):');
            for (const sub of field.itemFields) if (!sub.hidden) lines.push(formatField(sub, '      '));
        }
    }
    if (schema.actions && schema.actions.length > 0) {
        lines.push('', 'Actions (callable via call_action):');
        for (const action of schema.actions) lines.push(`  ${action.name} — ${action.label}`);
    }
    if (remainder) lines.push('', remainder);
    if (schema.docs) lines.push('', schema.docs);
    return lines.join('\n');
}
